import { useState, useEffect, useMemo, useRef, useCallback, Fragment } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { ChevronLeft, ChevronRight, Plus, CalendarDays, List, Clock, MessageSquare } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { toast } from "sonner";
import JobDialog from "@/components/JobDialog";
import { JOB_COLUMNS, CELL_CLASS, HEADER_CLASS } from "@/components/JobColumns";

function needsAttention(job) {
  if (job.passenger_count > 3) return true;
  if (job.special_instructions && job.special_instructions.trim().length > 0) return true;
  return false;
}

function getAttentionReasons(job) {
  const reasons = [];
  if (job.passenger_count > 3) reasons.push(`${job.passenger_count} riders`);
  if (job.special_instructions && job.special_instructions.trim().length > 0) {
    const lower = job.special_instructions.toLowerCase();
    if (lower.includes("child seat") || lower.includes("car seat") || lower.includes("booster")) {
      reasons.push("child seat");
    } else {
      reasons.push("special request");
    }
  }
  return reasons;
}

function shortAddr(addr) {
  const a = (addr || "").trim();
  if (!a) return "—";
  if (/\bjfk\b|\bkennedy\b/i.test(a) && !/\b(blvd|street|st|ave|avenue|rd|road|drive|dr)\b/i.test(a)) return "JFK";
  if (/\blga\b|\blaguardia\b|\bla\s*guardia\b/i.test(a)) return "LGA";
  if (/\bewr\b|\bnewark\b/i.test(a) && /airport|terminal|liberty|intl|international/i.test(a)) return "EWR";
  if (/\bhpn\b|\bwestchester\b/i.test(a) && !/\b(blvd|street|st|ave|avenue|rd|road)\b/i.test(a)) return "HPN";
  if (/\bteb\b|\bteterboro\b/i.test(a)) return "TEB";
  const seg = a.split(",")[0].trim();
  const boro = boroughTag(a);
  const label = seg.length > 18 ? seg.slice(0, 17) + "…" : seg;
  return boro ? `${label}, ${boro}` : label;
}

function boroughTag(addr) {
  const a = (addr || "").toLowerCase();
  if (/\bmanhattan\b/.test(a)) return "M";
  if (/\bnew\s*york,\s*ny\b|\bnew\s*york\s+ny\b/.test(a) && !/\b(brooklyn|queens|bronx|staten\s*island)\b/.test(a)) return "M";
  if (/\bbrooklyn\b/.test(a)) return "BK";
  if (/\bbronx\b/.test(a)) return "BX";
  if (/\bstaten\s*island\b/.test(a)) return "SI";
  if (/\bqueens\b|\bastoria\b|\bflushing\b|\bjackson\s*heights\b|\bjamaica\b|\blong\s*island\s*city\b|\belmhurst\b|\bcorona\b|\bwoodside\b/.test(a)) return "Q";
  return "";
}

function shortTime(dt) {
  if (!dt) return "—";
  try {
    const t = dt.split("T")[1] || "";
    const [hh, mm] = t.split(":").map(Number);
    if (isNaN(hh)) return "—";
    const ampm = hh >= 12 ? "p" : "a";
    const h12 = hh === 0 ? 12 : hh > 12 ? hh - 12 : hh;
    return `${h12}:${String(mm || 0).padStart(2, "0")}${ampm}`;
  } catch { return "—"; }
}

function RideExcerpt({ job, getDriverName, onClick }) {
  const time = shortTime(job.pickup_datetime);
  const pu = shortAddr(job.pickup_location);
  const doff = shortAddr(job.dropoff_location);
  const isUnassigned = job.status === "unassigned";
  const isCancelled = job.status === "cancelled";
  const isNoShow = job.status === "no_show";

  const driverName = job.driver_id ? getDriverName(job.driver_id) : "";
  const driverLabel = driverName
    ? `${job.driver_number ? `#${job.driver_number} — ` : ""}${driverName}`
    : "Unassigned";
  const statusLabel = {
    unassigned: "Unassigned", assigned: "Assigned", in_progress: "In Progress",
    completed: "Completed", cancelled: "Cancelled", no_show: "No-Show",
    arrived_at_pickup: "At Pickup", passenger_on_board: "On Board",
  }[job.status] || job.status || "—";
  const paymentLabel = {
    invoice_sent: "Invoice Sent", paid: "Paid", voided: "Voided", refunded: "Refunded",
  }[job.payment_status] || "—";

  const lineClass = isCancelled || isNoShow
    ? "text-muted-foreground/40 line-through"
    : isUnassigned
      ? "text-amber-400/80"
      : "text-foreground/70";

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <div
          className={`text-[11px] leading-[16px] truncate rounded px-0.5 cursor-pointer hover:bg-muted/40 ${lineClass}`}
          onClick={(e) => { e.stopPropagation(); onClick(job); }}
          data-testid={`month-ride-${job.id}`}
        >
          <span className="text-muted-foreground/60">{time}</span>
          <span className="text-muted-foreground/30"> · </span>
          {pu}<span className="text-muted-foreground/30"> → </span>{doff}
        </div>
      </TooltipTrigger>
      <TooltipContent side="bottom" align="start" className="bg-popover border-border text-xs p-2 max-w-[260px] space-y-1">
        <div className="font-medium text-foreground">{time === "—" ? "—" : job.pickup_datetime?.split("T")[1]?.slice(0, 5) || "—"}</div>
        <div className="text-foreground/80">{job.client_name || `${job.first_name || ""} ${job.last_name || ""}`.trim() || "—"}</div>
        <div className="text-muted-foreground truncate">{job.pickup_location || "—"}</div>
        <div className="text-muted-foreground truncate">→ {job.dropoff_location || "—"}</div>
        <div className={isUnassigned ? "text-amber-400" : "text-foreground/70"}>Driver: {driverLabel}</div>
        <div className="text-muted-foreground">Status: {statusLabel}</div>
        {job.payment_status && <div className="text-muted-foreground">Payment: {paymentLabel}</div>}
      </TooltipContent>
    </Tooltip>
  );
}

const STORAGE_KEY = "arrivo_day_col_order";

function getStoredColumnOrder() {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      const order = JSON.parse(stored);
      const validKeys = JOB_COLUMNS.map((c) => c.key);
      const filtered = order.filter((k) => validKeys.includes(k));
      const missing = validKeys.filter((k) => !filtered.includes(k));
      return [...filtered, ...missing];
    }
  } catch (e) { console.warn("Parse stored calendar column order:", e); }
  const priority = ["driver", "flag", "client"];
  const rest = JOB_COLUMNS.map((c) => c.key).filter((k) => !priority.includes(k));
  return [...priority, ...rest];
}

function DayView({ dayJobs, daySummary, getDriverName, highlightedJobId, setHighlightedJobId, onJobUpdated, contextMenu, setContextMenu, onCancel, onRevive, onEdit, onView, onEmailDriver, onSetStatus, unreadByJobId = {} }) {
  const [colOrder, setColOrder] = useState(getStoredColumnOrder);
  const [dragCol, setDragCol] = useState(null);
  const [dragOver, setDragOver] = useState(null);
  const [editingDriverJobId, setEditingDriverJobId] = useState(null);
  const [driverInput, setDriverInput] = useState("");
  const [driverSaving, setDriverSaving] = useState(false);

  const orderedCols = useMemo(() => colOrder.map((k) => JOB_COLUMNS.find((c) => c.key === k)).filter(Boolean), [colOrder]);

  const handleDragStart = (e, key) => {
    setDragCol(key);
    e.dataTransfer.effectAllowed = "move";
    e.dataTransfer.setData("text/plain", key);
  };

  const handleDragOver = (e, key) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    if (key !== dragCol) setDragOver(key);
  };

  const handleDrop = (e, targetKey) => {
    e.preventDefault();
    if (!dragCol || dragCol === targetKey) { setDragCol(null); setDragOver(null); return; }
    const newOrder = [...colOrder];
    const fromIdx = newOrder.indexOf(dragCol);
    const toIdx = newOrder.indexOf(targetKey);
    newOrder.splice(fromIdx, 1);
    newOrder.splice(toIdx, 0, dragCol);
    setColOrder(newOrder);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(newOrder));
    setDragCol(null);
    setDragOver(null);
  };

  const handleDragEnd = () => { setDragCol(null); setDragOver(null); };

  const openDriverEdit = (job) => {
    setEditingDriverJobId(job.id);
    setDriverInput(job.driver_number || "");
  };

  const saveDriverNumber = async (jobId) => {
    setDriverSaving(true);
    try {
      const num = driverInput.trim();
      if (num) {
        // Lookup driver by number, then assign
        try {
          const { data } = await api.get(`/drivers/lookup/${num}`);
          await api.post(`/jobs/${jobId}/assign`, { driver_id: data.driver.id, notify: false });
          await api.put(`/jobs/${jobId}`, { driver_number: num });
          toast.success(`Driver ${num} assigned`);
        } catch {
          // Driver not found in system, just save the number
          await api.put(`/jobs/${jobId}`, { driver_number: num });
          toast.success(`Driver # ${num} saved`);
        }
      } else {
        // Clear driver
        await api.post(`/jobs/${jobId}/unassign`);
        await api.put(`/jobs/${jobId}`, { driver_number: "" });
        toast.success("Driver removed");
      }
      setEditingDriverJobId(null);
      if (onJobUpdated) onJobUpdated();
    } catch (e) {
      toast.error("Failed to update driver");
    } finally {
      setDriverSaving(false);
    }
  };

  const cancelDriverEdit = () => {
    setEditingDriverJobId(null);
    setDriverInput("");
  };

  return (
    <div data-testid="calendar-day-view" className="space-y-4">
      {/* Summary bar */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3 px-4 py-2.5 rounded-lg border border-border bg-card flex-1" data-testid="day-summary-bar">
          <span className="text-sm font-medium text-foreground">{daySummary.total} {daySummary.total === 1 ? "job" : "jobs"}</span>
          {daySummary.open > 0 && (<><span className="text-muted-foreground/40">·</span><span className="text-sm font-medium text-amber-400">{daySummary.open} open</span></>)}
          {daySummary.completed > 0 && (<><span className="text-muted-foreground/40">·</span><span className="text-sm font-medium text-emerald-400">{daySummary.completed} completed</span></>)}
          {daySummary.no_show > 0 && (<><span className="text-muted-foreground/40">·</span><span className="text-sm font-bold text-red-500 tracking-wider">{daySummary.no_show} NS</span></>)}
          {daySummary.cancelled > 0 && (<><span className="text-muted-foreground/40">·</span><span className="text-sm font-medium text-red-400">{daySummary.cancelled} cancelled</span></>)}
          {daySummary.flagged > 0 && (<><span className="text-muted-foreground/40">·</span><span className="inline-block w-2 h-2 rounded-sm bg-orange-400 rotate-45" /><span className="text-sm font-medium text-orange-400">{daySummary.flagged} flagged</span></>)}
        </div>
        <span className="text-[10px] text-muted-foreground/50 ml-3">Drag columns to reorder</span>
      </div>

      {/* Table */}
      <div className="border border-border rounded-lg overflow-x-auto">
        <table className="w-full caption-bottom text-sm min-w-[900px]">
          <thead>
            <tr className="border-b bg-muted/20">
              <th className={`w-6 ${HEADER_CLASS} text-center align-middle text-muted-foreground`} aria-label="Driver notes" title="Driver notes">
                <MessageSquare className="w-3 h-3 inline-block" aria-hidden="true" />
              </th>
              {orderedCols.map((col) => (
                <th
                  key={col.key}
                  draggable
                  onDragStart={(e) => handleDragStart(e, col.key)}
                  onDragOver={(e) => handleDragOver(e, col.key)}
                  onDrop={(e) => handleDrop(e, col.key)}
                  onDragEnd={handleDragEnd}
                  className={`h-8 ${HEADER_CLASS} text-left align-middle font-medium text-muted-foreground cursor-grab active:cursor-grabbing select-none transition-colors ${col.w || ""} ${
                    dragCol === col.key ? "opacity-40" : ""
                  } ${dragOver === col.key ? "bg-primary/10 border-l-2 border-primary" : ""}`}
                  data-testid={`day-col-header-${col.key}`}
                >
                  {col.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {dayJobs.length === 0 ? (
              <tr className="border-b"><td colSpan={orderedCols.length + 1} className="text-center py-12 text-muted-foreground p-2">No jobs scheduled for this day</td></tr>
            ) : (
              dayJobs.map((job) => {
                const isCancelled = job.status === "cancelled";
                const isHighlighted = highlightedJobId === job.id;
                const unread = unreadByJobId[job.id] || 0;
                return (
                <tr
                  key={job.id}
                  className={`border-b cursor-pointer transition-colors ${isCancelled ? "bg-red-500/5 text-red-400/80 line-through hover:bg-red-500/10" : "hover:bg-muted/30"} ${isHighlighted ? "bg-primary/10 ring-1 ring-primary/30" : ""}`}
                  onClick={() => setHighlightedJobId(isHighlighted ? null : job.id)}
                  onContextMenu={(e) => {
                    e.preventDefault();
                    setHighlightedJobId(job.id);
                    setContextMenu({ x: e.clientX, y: e.clientY, job });
                  }}
                  data-testid={`day-job-row-${job.id}`}
                >
                  <td
                    className={`${CELL_CLASS} align-middle w-6 px-1`}
                    onClick={(e) => { e.stopPropagation(); if (unread > 0) onView(job); }}
                    title={unread > 0 ? `${unread} unread driver note${unread > 1 ? "s" : ""}` : ""}
                    data-testid={`day-notes-cell-${job.id}`}
                  >
                    {unread > 0 && (
                      <span
                        className="inline-flex items-center gap-0.5 text-amber-400"
                        data-testid={`day-notes-badge-${job.id}`}
                      >
                        <MessageSquare className="w-3 h-3" />
                        <span className="text-[10px] font-semibold leading-none">{unread}</span>
                      </span>
                    )}
                  </td>
                  {orderedCols.map((col) => {
                    // Driver cell: inline editable
                    if (col.isDriverCell) {
                      const isEditing = editingDriverJobId === job.id;
                      const hasDriver = !!job.driver_id || !!job.driver_number;
                      return (
                        <td
                          key={col.key}
                          className={`${CELL_CLASS} align-middle ${col.w || ""} ${!hasDriver && !isEditing && !isCancelled ? "bg-amber-500/20" : ""}`}
                          onClick={(e) => { e.stopPropagation(); if (!isEditing && !isCancelled) openDriverEdit(job); }}
                          data-testid={`day-driver-cell-${job.id}`}
                        >
                          {isEditing ? (
                            <input
                              autoFocus
                              value={driverInput}
                              onChange={(e) => setDriverInput(e.target.value)}
                              onKeyDown={(e) => {
                                if (e.key === "Enter") { e.preventDefault(); saveDriverNumber(job.id); }
                                if (e.key === "Escape") cancelDriverEdit();
                              }}
                              onBlur={() => saveDriverNumber(job.id)}
                              disabled={driverSaving}
                              className="w-full bg-muted/40 border border-primary/50 rounded px-1.5 py-0.5 text-sm text-center text-cyan-300 font-semibold outline-none focus:border-primary"
                              style={{ fontFamily: "'IBM Plex Sans', monospace" }}
                              placeholder="#"
                              data-testid={`day-driver-input-${job.id}`}
                            />
                          ) : (
                            col.render(job, getDriverName)
                          )}
                        </td>
                      );
                    }
                    // Email cell: clickable when driver assigned
                    if (col.isEmailCell) {
                      return (
                        <td
                          key={col.key}
                          className={`${CELL_CLASS} align-middle ${col.w || ""}`}
                          onClick={(e) => { e.stopPropagation(); if (job.driver_id) onEmailDriver(job); }}
                          data-testid={`day-email-cell-${job.id}`}
                        >
                          {col.render(job)}
                        </td>
                      );
                    }
                    // Payment cell: informational only
                    if (col.isPaymentCell) {
                      return (
                        <td key={col.key} className={`${CELL_CLASS} align-middle ${col.w || ""}`} data-testid={`day-payment-cell-${job.id}`}>
                          {col.render(job)}
                        </td>
                      );
                    }
                    return (
                      <td
                        key={col.key}
                        className={`${CELL_CLASS} align-middle ${col.w || ""} ${col.cellClass ? col.cellClass(job) : ""}`}
                      >
                        {col.render(job, getDriverName)}
                      </td>
                    );
                  })}
                </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {/* Right-click context menu */}
      <JobContextMenu
        contextMenu={contextMenu}
        setContextMenu={setContextMenu}
        onView={onView}
        onEdit={onEdit}
        onCancel={onCancel}
        onRevive={onRevive}
        onSetStatus={onSetStatus}
      />
    </div>
  );
}

function JobContextMenu({ contextMenu, setContextMenu, onView, onEdit, onCancel, onRevive, onSetStatus }) {
  if (!contextMenu) return null;
  return (
    <div
      className="fixed z-50 min-w-[160px] rounded-md border border-border bg-popover shadow-lg py-1"
      style={{ top: contextMenu.y, left: contextMenu.x }}
      data-testid="context-menu"
    >
      <div className="px-3 py-1.5 text-xs text-muted-foreground border-b border-border mb-1 truncate max-w-[200px]">
        {contextMenu.job.client_name}
      </div>
      <button
        className="w-full text-left px-3 py-1.5 text-sm hover:bg-muted/40 transition-colors"
        onClick={() => { onView(contextMenu.job); setContextMenu(null); }}
        data-testid="context-view"
      >
        View
      </button>
      <button
        className="w-full text-left px-3 py-1.5 text-sm hover:bg-muted/40 transition-colors"
        onClick={() => { onEdit(contextMenu.job); setContextMenu(null); }}
        data-testid="context-edit"
      >
        Edit
      </button>
      {(contextMenu.job.status === "assigned" || contextMenu.job.status === "completed") && (
        <>
          {contextMenu.job.status === "assigned" && (
            <button
              className="w-full text-left px-3 py-1.5 text-sm text-emerald-400 hover:bg-emerald-500/10 transition-colors"
              onClick={() => { onSetStatus(contextMenu.job, "completed"); setContextMenu(null); }}
              data-testid="context-completed"
            >
              Mark Completed
            </button>
          )}
          <button
            className="w-full text-left px-3 py-1.5 text-sm text-red-500 hover:bg-red-500/10 transition-colors"
            onClick={() => { onSetStatus(contextMenu.job, "no_show"); setContextMenu(null); }}
            data-testid="context-no-show"
          >
            No-Show
          </button>
        </>
      )}
      {contextMenu.job.status === "cancelled" ? (
        <button
          className="w-full text-left px-3 py-1.5 text-sm text-emerald-400 hover:bg-emerald-500/10 transition-colors"
          onClick={() => onRevive(contextMenu.job)}
          data-testid="context-revive"
        >
          Revive
        </button>
      ) : (
        <button
          className="w-full text-left px-3 py-1.5 text-sm text-red-400 hover:bg-red-500/10 transition-colors"
          onClick={() => { onCancel(contextMenu.job); setContextMenu(null); }}
          data-testid="context-cancel"
        >
          Cancel Job
        </button>
      )}
    </div>
  );
}

function UpcomingView({ upcomingJobs, getDriverName, onJobUpdated, onView, onEdit, onEmailDriver, contextMenu, setContextMenu, onCancel, onRevive, onSetStatus }) {
  const [colOrder, setColOrder] = useState(getStoredColumnOrder);
  const [dragCol, setDragCol] = useState(null);
  const [dragOver, setDragOver] = useState(null);
  const [editingDriverJobId, setEditingDriverJobId] = useState(null);
  const [driverInput, setDriverInput] = useState("");
  const [driverSaving, setDriverSaving] = useState(false);

  const orderedCols = useMemo(() => colOrder.map((k) => JOB_COLUMNS.find((c) => c.key === k)).filter(Boolean), [colOrder]);

  const grouped = useMemo(() => {
    const map = new Map();
    for (const job of upcomingJobs) {
      const dk = job.pickup_datetime?.slice(0, 10) || "unknown";
      if (!map.has(dk)) map.set(dk, []);
      map.get(dk).push(job);
    }
    return [...map.entries()];
  }, [upcomingJobs]);

  const handleDragStart = (e, key) => {
    setDragCol(key);
    e.dataTransfer.effectAllowed = "move";
    e.dataTransfer.setData("text/plain", key);
  };

  const handleDragOver = (e, key) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    if (key !== dragCol) setDragOver(key);
  };

  const handleDrop = (e, targetKey) => {
    e.preventDefault();
    if (!dragCol || dragCol === targetKey) { setDragCol(null); setDragOver(null); return; }
    const newOrder = [...colOrder];
    const fromIdx = newOrder.indexOf(dragCol);
    const toIdx = newOrder.indexOf(targetKey);
    newOrder.splice(fromIdx, 1);
    newOrder.splice(toIdx, 0, dragCol);
    setColOrder(newOrder);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(newOrder));
    setDragCol(null);
    setDragOver(null);
  };

  const handleDragEnd = () => { setDragCol(null); setDragOver(null); };

  const openDriverEdit = (job) => {
    setEditingDriverJobId(job.id);
    setDriverInput(job.driver_number || "");
  };

  const saveDriverNumber = async (jobId) => {
    setDriverSaving(true);
    try {
      const num = driverInput.trim();
      if (num) {
        try {
          const { data } = await api.get(`/drivers/lookup/${num}`);
          await api.post(`/jobs/${jobId}/assign`, { driver_id: data.driver.id, notify: false });
          await api.put(`/jobs/${jobId}`, { driver_number: num });
          toast.success(`Driver ${num} assigned`);
        } catch {
          await api.put(`/jobs/${jobId}`, { driver_number: num });
          toast.success(`Driver # ${num} saved`);
        }
      } else {
        await api.post(`/jobs/${jobId}/unassign`);
        await api.put(`/jobs/${jobId}`, { driver_number: "" });
        toast.success("Driver removed");
      }
      setEditingDriverJobId(null);
      if (onJobUpdated) onJobUpdated();
    } catch (e) {
      toast.error("Failed to update driver");
    } finally {
      setDriverSaving(false);
    }
  };

  const cancelDriverEdit = () => {
    setEditingDriverJobId(null);
    setDriverInput("");
  };

  const formatGroupDate = (dateStr) => {
    try {
      const [y, m, d] = dateStr.split("-").map(Number);
      const dt = new Date(y, m - 1, d);
      return `${DAY_NAMES_FULL[dt.getDay()]}, ${MONTH_NAMES[dt.getMonth()]} ${d}, ${y}`;
    } catch { return dateStr; }
  };

  return (
    <div data-testid="calendar-upcoming" className="border border-border rounded-lg overflow-x-auto">
      <table className="w-full caption-bottom text-xs min-w-[900px]">
        <thead>
          <tr className="border-b bg-muted/20">
            {orderedCols.map((col) => (
              <th
                key={col.key}
                draggable
                onDragStart={(e) => handleDragStart(e, col.key)}
                onDragOver={(e) => handleDragOver(e, col.key)}
                onDrop={(e) => handleDrop(e, col.key)}
                onDragEnd={handleDragEnd}
                className={`h-8 ${HEADER_CLASS} text-left align-middle font-medium text-muted-foreground cursor-grab active:cursor-grabbing select-none transition-colors ${col.w || ""} ${
                  dragCol === col.key ? "opacity-40" : ""
                } ${dragOver === col.key ? "bg-primary/10 border-l-2 border-primary" : ""}`}
                data-testid={`upcoming-col-header-${col.key}`}
              >
                {col.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {upcomingJobs.length === 0 ? (
            <tr className="border-b"><td colSpan={orderedCols.length} className="text-center py-12 text-muted-foreground">No upcoming rides.</td></tr>
          ) : grouped.map(([dateStr, jobs]) => {
            const open = jobs.filter((j) => j.status === "unassigned").length;
            const total = jobs.length;
            return (
              <Fragment key={dateStr}>
                <tr className="bg-muted/10" data-testid={`upcoming-date-header-${dateStr}`}>
                  <td colSpan={orderedCols.length} className="px-3 py-2 border-b border-border">
                    <span className="text-xs font-medium text-foreground/80">{formatGroupDate(dateStr)}</span>
                    <span className="text-[10px] text-muted-foreground/60 ml-2">
                      · {total} {total === 1 ? "ride" : "rides"}{open > 0 ? ` · ${open} open` : ""}
                    </span>
                  </td>
                </tr>
                {jobs.map((job) => {
                  const isUnassigned = job.status === "unassigned";
                  const isCancelled = job.status === "cancelled";
                  return (
                    <tr
                      key={job.id}
                      className={`border-b cursor-pointer transition-colors ${isCancelled ? "opacity-40 line-through" : ""} ${isUnassigned && !isCancelled ? "ring-1 ring-inset ring-amber-500/20" : ""} hover:bg-muted/30`}
                      onClick={() => onView(job)}
                      onContextMenu={(e) => {
                        e.preventDefault();
                        setContextMenu({ x: e.clientX, y: e.clientY, job });
                      }}
                      data-testid={`upcoming-row-${job.id}`}
                    >
                      {orderedCols.map((col) => {
                        if (col.isDriverCell) {
                          const isEditing = editingDriverJobId === job.id;
                          const hasDriver = !!job.driver_id || !!job.driver_number;
                          return (
                            <td
                              key={col.key}
                              className={`${CELL_CLASS} align-middle ${col.w || ""} ${!hasDriver && !isEditing && !isCancelled ? "bg-amber-500/20" : ""}`}
                              onClick={(e) => { e.stopPropagation(); if (!isEditing && !isCancelled) openDriverEdit(job); }}
                              data-testid={`upcoming-driver-cell-${job.id}`}
                            >
                              {isEditing ? (
                                <input
                                  autoFocus
                                  value={driverInput}
                                  onChange={(e) => setDriverInput(e.target.value)}
                                  onKeyDown={(e) => {
                                    if (e.key === "Enter") { e.preventDefault(); saveDriverNumber(job.id); }
                                    if (e.key === "Escape") cancelDriverEdit();
                                  }}
                                  onBlur={() => saveDriverNumber(job.id)}
                                  disabled={driverSaving}
                                  className="w-full bg-muted/40 border border-primary/50 rounded px-1.5 py-0.5 text-sm text-center text-cyan-300 font-semibold outline-none focus:border-primary"
                                  style={{ fontFamily: "'IBM Plex Sans', monospace" }}
                                  placeholder="#"
                                  data-testid={`upcoming-driver-input-${job.id}`}
                                />
                              ) : (
                                col.render(job, getDriverName)
                              )}
                            </td>
                          );
                        }
                        if (col.isEmailCell) {
                          return (
                            <td
                              key={col.key}
                              className={`${CELL_CLASS} align-middle ${col.w || ""}`}
                              onClick={(e) => { e.stopPropagation(); if (job.driver_id) onEmailDriver(job); }}
                              data-testid={`upcoming-email-cell-${job.id}`}
                            >
                              {col.render(job)}
                            </td>
                          );
                        }
                        if (col.isPaymentCell) {
                          return (
                            <td key={col.key} className={`${CELL_CLASS} align-middle ${col.w || ""}`} data-testid={`upcoming-payment-cell-${job.id}`}>
                              {col.render(job)}
                            </td>
                          );
                        }
                        return (
                          <td
                            key={col.key}
                            className={`${CELL_CLASS} align-middle ${col.w || ""} ${col.cellClass ? col.cellClass(job) : ""}`}
                          >
                            {col.render(job, getDriverName)}
                          </td>
                        );
                      })}
                    </tr>
                  );
                })}
              </Fragment>
            );
          })}
        </tbody>
      </table>
      <JobContextMenu
        contextMenu={contextMenu}
        setContextMenu={setContextMenu}
        onView={onView}
        onEdit={onEdit}
        onCancel={onCancel}
        onRevive={onRevive}
        onSetStatus={onSetStatus}
      />
    </div>
  );
}


const DAY_NAMES = ["SUN", "MON", "TUE", "WED", "THU", "FRI", "SAT"];
const DAY_NAMES_FULL = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];
const MONTH_NAMES = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];

function formatMilFull(h, m) { return String(h).padStart(2, "0") + ":" + String(m).padStart(2, "0"); }
function dateKey(date) { return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`; }


export default function CalendarPage() {
  const [jobs, setJobs] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [filter, setFilter] = useState("all");
  const [view, setView] = useState("month");
  const [highlightedJobId, setHighlightedJobId] = useState(null);
  const [editingJob, setEditingJob] = useState(null);
  const [viewingJob, setViewingJob] = useState(null);
  const [showJobDialog, setShowJobDialog] = useState(false);
  const [jobDialogReadOnly, setJobDialogReadOnly] = useState(false);
  const [newJobDate, setNewJobDate] = useState(null);
  const [unreadByJobId, setUnreadByJobId] = useState({});
  useEffect(() => { loadData(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Poll dispatch unread-notes summary every 60s; lightweight aggregate only.
  useEffect(() => {
    let cancelled = false;
    const fetchUnread = async () => {
      try {
        const { data } = await api.get("/dispatch/jobs-with-unread-notes");
        if (cancelled) return;
        const map = {};
        for (const row of data?.jobs || []) {
          if (row?.job_id) map[row.job_id] = row.unread_count || 0;
        }
        setUnreadByJobId(map);
      } catch (e) { console.warn("Fetch unread notes:", e); }
    };
    fetchUnread();
    const t = setInterval(fetchUnread, 60000);
    return () => { cancelled = true; clearInterval(t); };
  }, []);

  const loadData = async () => {
    try {
      const [jobsRes, driversRes, vehiclesRes] = await Promise.all([
        api.get("/jobs"), api.get("/drivers"), api.get("/vehicles"),
      ]);
      setJobs(jobsRes.data);
      setDrivers(driversRes.data);
      setVehicles(vehiclesRes.data);
    } catch (e) { console.error("Load error:", e); }
  };

  const year = currentDate.getFullYear();
  const month = currentDate.getMonth();

  // === MONTH VIEW DAYS ===
  const calendarDays = useMemo(() => {
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const startDow = firstDay.getDay();
    const days = [];
    for (let i = startDow - 1; i >= 0; i--) days.push({ date: new Date(year, month, -i), isCurrentMonth: false });
    for (let i = 1; i <= lastDay.getDate(); i++) days.push({ date: new Date(year, month, i), isCurrentMonth: true });
    const remaining = 7 - (days.length % 7);
    if (remaining < 7) for (let i = 1; i <= remaining; i++) days.push({ date: new Date(year, month + 1, i), isCurrentMonth: false });
    return days;
  }, [year, month]);

  const filteredJobs = useMemo(() => {
    if (filter === "all") return jobs;
    if (filter === "unassigned") return jobs.filter((j) => j.status === "unassigned");
    if (filter === "assigned") return jobs.filter((j) => ["assigned", "in_progress"].includes(j.status));
    return jobs;
  }, [jobs, filter]);

  const getJobsForDate = useCallback((date) => {
    const dk = dateKey(date);
    return filteredJobs.filter((j) => j.pickup_datetime && j.pickup_datetime.startsWith(dk));
  }, [filteredJobs]);

  const isToday = (date) => {
    const today = new Date();
    return date.getDate() === today.getDate() && date.getMonth() === today.getMonth() && date.getFullYear() === today.getFullYear();
  };

  // === DAY VIEW: jobs for current date, cancelled sink to bottom ===
  const dayJobs = useMemo(() => {
    const sorted = getJobsForDate(currentDate).sort((a, b) => (a.pickup_datetime || "").localeCompare(b.pickup_datetime || ""));
    const active = sorted.filter((j) => j.status !== "cancelled");
    const cancelled = sorted.filter((j) => j.status === "cancelled");
    return [...active, ...cancelled];
  }, [currentDate, getJobsForDate]);

  const dayLabel = useMemo(() => {
    return `${DAY_NAMES_FULL[currentDate.getDay()]}, ${MONTH_NAMES[currentDate.getMonth()]} ${currentDate.getDate()}, ${currentDate.getFullYear()}`;
  }, [currentDate]);

  const daySummary = useMemo(() => {
    const total = dayJobs.length;
    const open = dayJobs.filter((j) => j.status === "unassigned").length;
    const flagged = dayJobs.filter(needsAttention).length;
    const cancelled = dayJobs.filter((j) => j.status === "cancelled").length;
    const completed = dayJobs.filter((j) => j.status === "completed").length;
    const no_show = dayJobs.filter((j) => j.status === "no_show").length;
    return { total, open, flagged, cancelled, completed, no_show };
  }, [dayJobs]);

  // === UPCOMING VIEW: all future jobs from today onward ===
  const upcomingJobs = useMemo(() => {
    const todayStr = dateKey(new Date());
    return filteredJobs
      .filter((j) => j.pickup_datetime && j.pickup_datetime.slice(0, 10) >= todayStr)
      .sort((a, b) => (a.pickup_datetime || "").localeCompare(b.pickup_datetime || ""));
  }, [filteredJobs]);

  const upcomingSummary = useMemo(() => {
    const total = upcomingJobs.length;
    const open = upcomingJobs.filter((j) => j.status === "unassigned").length;
    return { total, open };
  }, [upcomingJobs]);

  // === NAVIGATION ===
  const prev = () => {
    if (view === "month") setCurrentDate(new Date(year, month - 1, 1));
    else if (view === "day") { const d = new Date(currentDate); d.setDate(d.getDate() - 1); setCurrentDate(d); }
  };

  const next = () => {
    if (view === "month") setCurrentDate(new Date(year, month + 1, 1));
    else if (view === "day") { const d = new Date(currentDate); d.setDate(d.getDate() + 1); setCurrentDate(d); }
  };

  const goToday = () => setCurrentDate(new Date());

  const goToDay = (date) => {
    setCurrentDate(date);
    setView("day");
  };

  const getDriverName = (driverId) => drivers.find((dr) => dr.id === driverId)?.name || "";

  const openNewJob = useCallback((defaultDate) => {
    setEditingJob(null);
    setNewJobDate(defaultDate || null);
    setShowJobDialog(true);
  }, []);

  const handleDayClick = (date, hour) => {
    if (view === "month") {
      goToDay(date);
    } else {
      const h = hour !== undefined ? hour : 9;
      openNewJob(`${dateKey(date)}T${String(h).padStart(2, "0")}:00`);
    }
  };

  const handleJobSaved = ({ isEdit } = {}) => {
    setShowJobDialog(false);
    setEditingJob(null);
    setViewingJob(null);
    setNewJobDate(null);
    setJobDialogReadOnly(false);
    setHighlightedJobId(null);
    // After creating a new job (not editing), jump to Upcoming so the user
    // immediately sees their fresh entry in context with other future rides.
    if (!isEdit) setView("upcoming");
    loadData();
  };

  // === V/E/R/DEL KEYS — based on highlighted job ===
  const getHighlightedJob = useCallback(() => {
    if (!highlightedJobId) return null;
    return dayJobs.find((j) => j.id === highlightedJobId) || null;
  }, [highlightedJobId, dayJobs]);

  useEffect(() => {
    const handler = (e) => {
      if (e.target.tagName === "INPUT" || e.target.tagName === "TEXTAREA" || e.target.tagName === "SELECT" || e.target.isContentEditable) return;
      const hJob = getHighlightedJob();
      if (!hJob) return;

      if (e.key === "v" || e.key === "V") {
        e.preventDefault();
        setViewingJob(hJob);
        setEditingJob(null);
        setJobDialogReadOnly(true);
        setShowJobDialog(true);
      }
      if (e.key === "e" || e.key === "E") {
        e.preventDefault();
        setEditingJob(hJob);
        setViewingJob(null);
        setJobDialogReadOnly(false);
        setShowJobDialog(true);
      }
      if (e.key === "r" || e.key === "R") {
        e.preventDefault();
        toast("Create return trip?", {
          action: {
            label: "Yes",
            onClick: () => {
              const returnJob = {
                first_name: hJob.first_name || "",
                last_name: hJob.last_name || "",
                client_phone: hJob.client_phone || "",
                client_email: hJob.client_email || "",
                account_number: hJob.account_number || "",
                passenger_count: hJob.passenger_count || 1,
                luggage: hJob.luggage || "",
                vehicle_class: hJob.vehicle_class || "sedan",
                special_services: hJob.special_services || [],
                child_seats_rear: hJob.child_seats_rear || 0,
                child_seats_front: hJob.child_seats_front || 0,
                child_seats_booster: hJob.child_seats_booster || 0,
                pickup_location: hJob.dropoff_location || "",
                dropoff_location: hJob.pickup_location || "",
                pricing_mode: hJob.pricing_mode || "all_inclusive",
              };
              setEditingJob(returnJob);
              setViewingJob(null);
              setJobDialogReadOnly(false);
              setNewJobDate(null);
              setShowJobDialog(true);
            },
          },
        });
      }
      if ((e.key === "Delete" || (e.key === "Backspace" && !e.metaKey))) {
        if (jobDialogReadOnly && viewingJob && viewingJob.status !== "cancelled") {
          e.preventDefault();
          setCancelConfirmJob(viewingJob);
        } else if (hJob.status !== "cancelled") {
          e.preventDefault();
          setCancelConfirmJob(hJob);
        }
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [getHighlightedJob, jobDialogReadOnly, viewingJob]);

  const [cancelConfirmJob, setCancelConfirmJob] = useState(null);
  const [contextMenu, setContextMenu] = useState(null);

  const cancelJob = async (job) => {
    try {
      const { data } = await api.put(`/jobs/${job.id}`, { status: "cancelled" });
      toast.success("Job cancelled");
      if (data?._warning) toast.warning(data._warning, { duration: 8000 });
      setCancelConfirmJob(null);
      setHighlightedJobId(null);
      setContextMenu(null);
      loadData();
    } catch { toast.error("Failed to cancel"); }
  };

  const reviveJob = async (job) => {
    try {
      const newStatus = job.driver_id ? "assigned" : "unassigned";
      await api.put(`/jobs/${job.id}`, { status: newStatus });
      toast.success("Job revived");
      setHighlightedJobId(null);
      setContextMenu(null);
      loadData();
    } catch { toast.error("Failed to revive"); }
  };

  const emailDriver = async (job) => {
    if (!job.driver_id) return;
    toast("Email job details to driver?", {
      action: {
        label: "Send",
        onClick: async () => {
          try {
            const { data } = await api.post(`/jobs/${job.id}/email-driver`);
            if (data.status === "sent") toast.success("Email sent to driver");
            else if (data.status === "skipped") toast.message(data.message || "Email skipped");
            else toast.message("Done");
          } catch (err) {
            toast.error(err.response?.data?.detail || "Failed to send email");
          }
        },
      },
    });
  };

  // Close context menu on click anywhere
  useEffect(() => {
    if (!contextMenu) return;
    const handler = () => setContextMenu(null);
    window.addEventListener("click", handler);
    return () => window.removeEventListener("click", handler);
  }, [contextMenu]);

  const navLabel = view === "month" ? `${MONTH_NAMES[month]} ${year}` : view === "upcoming" ? "Upcoming" : dayLabel;

  return (
    <div data-testid="calendar-page" className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-3xl tracking-tight font-light" style={{ fontFamily: "'Manrope', sans-serif" }}>
            Dispatch Calendar
          </h1>
          <p className="text-sm text-muted-foreground mt-1">Visual overview of all scheduled jobs</p>
        </div>
        <div className="flex items-center gap-3">
          {/* View Toggle — Month / Week / Day */}
          <div className="inline-flex items-center rounded-lg border border-border bg-card p-0.5" data-testid="calendar-view-toggle">
            {[
              { key: "upcoming", label: "Upcoming", icon: List },
              { key: "day", label: "Day", icon: Clock },
              { key: "month", label: "Month", icon: CalendarDays },
            ].map(({ key, label, icon: Icon }) => (
              <button
                key={key}
                onClick={() => setView(key)}
                className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-all ${
                  view === key ? "bg-primary text-primary-foreground shadow-sm" : "text-muted-foreground hover:text-foreground"
                }`}
                data-testid={`calendar-view-${key}`}
              >
                <Icon className="w-3.5 h-3.5" />
                {label}
              </button>
            ))}
          </div>

          <Select value={filter} onValueChange={setFilter}>
            <SelectTrigger className="w-40 bg-card border-border" data-testid="calendar-filter">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Jobs</SelectItem>
              <SelectItem value="unassigned">Unassigned Only</SelectItem>
              <SelectItem value="assigned">Assigned Only</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Navigation + Today + New Job */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          {view !== "upcoming" && (
            <Button variant="ghost" size="sm" onClick={prev} data-testid="calendar-prev">
              <ChevronLeft className="w-4 h-4" />
            </Button>
          )}
          <h2 className="text-lg font-medium min-w-[260px] text-center" style={{ fontFamily: "'Manrope', sans-serif" }}>
            {navLabel}
            {view === "upcoming" && upcomingSummary.total > 0 && (
              <span className="text-sm font-normal text-muted-foreground ml-3">
                {upcomingSummary.total} ride{upcomingSummary.total !== 1 ? "s" : ""}
                {upcomingSummary.open > 0 && <span className="text-amber-400 ml-1.5">· {upcomingSummary.open} unassigned</span>}
              </span>
            )}
          </h2>
          {view !== "upcoming" && (
            <Button variant="ghost" size="sm" onClick={next} data-testid="calendar-next">
              <ChevronRight className="w-4 h-4" />
            </Button>
          )}
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={goToday} className="border-border" data-testid="calendar-today">
            Today
          </Button>
          <Button
            size="sm"
            onClick={() => openNewJob(view === "day" ? `${dateKey(currentDate)}T09:00` : null)}
            className="bg-amber-500 hover:bg-amber-600 text-black font-semibold"
            data-testid="calendar-new-job"
          >
            <Plus className="w-4 h-4 mr-1" /> New Job
          </Button>
        </div>
      </div>

      {/* Legend — Day View only */}
      {view === "day" && (
      <div className="flex items-center gap-4 text-xs flex-wrap">
        <div className="flex items-center gap-1.5"><span className="w-3 h-3 rounded-sm bg-amber-500/30 border border-amber-500/50" /><span className="text-muted-foreground">Unassigned</span></div>
        <div className="flex items-center gap-1.5"><span className="w-3 h-3 rounded-sm bg-emerald-500/30 border border-emerald-500/50" /><span className="text-muted-foreground">Completed</span></div>
        <div className="flex items-center gap-1.5"><span className="w-3 h-3 rounded-sm bg-blue-500/30 border border-blue-500/50" /><span className="text-muted-foreground">In Progress</span></div>
        <div className="flex items-center gap-1.5"><span className="text-[10px] font-bold text-red-500 tracking-wider px-1">NS</span><span className="text-muted-foreground">No-Show</span></div>
      </div>
      )}

      {/* ========== MONTH VIEW ========== */}
      {view === "month" && (
        <div className="border border-border rounded-lg overflow-x-auto">
          <div className="dispatch-calendar bg-muted/30 min-w-[600px]">
            {DAY_NAMES.map((d) => (
              <div key={d} className="text-center py-2 text-xs font-medium text-muted-foreground uppercase tracking-wider">{d}</div>
            ))}
          </div>
          <TooltipProvider delayDuration={400}>
          <div className="dispatch-calendar min-w-[600px]" data-testid="calendar-grid-month">
            {calendarDays.map((day) => {
              const dJobs = getJobsForDate(day.date);
              const sorted = [...dJobs].sort((a, b) => (a.pickup_datetime || "").localeCompare(b.pickup_datetime || ""));
              const active = sorted.filter((j) => j.status !== "cancelled" && j.status !== "no_show");
              const rest = sorted.filter((j) => j.status === "cancelled" || j.status === "no_show");
              const ordered = [...active, ...rest];
              const visible = ordered.slice(0, 3);
              const moreCount = ordered.length - visible.length;
              return (
                <div
                  key={dateKey(day.date)}
                  className={`calendar-day ${isToday(day.date) ? "today" : ""} ${!day.isCurrentMonth ? "other-month" : ""} flex flex-col cursor-pointer overflow-hidden`}
                  onClick={() => goToDay(day.date)}
                  data-testid={`calendar-day-${dateKey(day.date)}`}
                >
                  <div className={`text-sm font-medium px-0.5 ${isToday(day.date) ? "text-primary" : "text-muted-foreground"}`}>{day.date.getDate()}</div>
                  {visible.length > 0 && (
                    <div className="flex flex-col gap-px px-0.5 flex-1 overflow-hidden">
                      {visible.map((job) => (
                        <RideExcerpt
                          key={job.id}
                          job={job}
                          getDriverName={getDriverName}
                          onClick={(j) => { setViewingJob(j); setEditingJob(null); setJobDialogReadOnly(true); setShowJobDialog(true); }}
                        />
                      ))}
                      {moreCount > 0 && (
                        <div className="text-[10px] text-muted-foreground/50 px-0.5 leading-tight" data-testid={`month-more-${dateKey(day.date)}`}>+{moreCount} more</div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
          </TooltipProvider>
        </div>
      )}

      {/* ========== UPCOMING VIEW ========== */}
      {view === "upcoming" && (
        <UpcomingView
          upcomingJobs={upcomingJobs}
          getDriverName={getDriverName}
          onJobUpdated={loadData}
          onView={(job) => { setViewingJob(job); setEditingJob(null); setJobDialogReadOnly(true); setShowJobDialog(true); }}
          onEdit={(job) => { setEditingJob(job); setViewingJob(null); setJobDialogReadOnly(false); setShowJobDialog(true); }}
          onEmailDriver={emailDriver}
          contextMenu={contextMenu}
          setContextMenu={setContextMenu}
          onCancel={(job) => setCancelConfirmJob(job)}
          onRevive={reviveJob}
          onSetStatus={async (job, status) => {
            try {
              await api.put(`/jobs/${job.id}`, { status });
              toast.success(status === "no_show" ? "Marked as no-show" : "Marked completed");
              loadData();
            } catch { toast.error("Failed to update"); }
          }}
        />
      )}

      {/* ========== DAY VIEW ========== */}
      {view === "day" && (
        <DayView
          dayJobs={dayJobs}
          daySummary={daySummary}
          getDriverName={getDriverName}
          highlightedJobId={highlightedJobId}
          setHighlightedJobId={setHighlightedJobId}
          onJobUpdated={loadData}
          contextMenu={contextMenu}
          setContextMenu={setContextMenu}
          onCancel={(job) => setCancelConfirmJob(job)}
          onRevive={reviveJob}
          onSetStatus={async (job, status) => {
            try {
              await api.put(`/jobs/${job.id}`, { status });
              toast.success(status === "no_show" ? "Marked as no-show" : "Marked completed");
              setHighlightedJobId(null);
              loadData();
            } catch { toast.error("Failed to update"); }
          }}
          onView={(job) => { setViewingJob(job); setEditingJob(null); setJobDialogReadOnly(true); setShowJobDialog(true); }}
          onEdit={(job) => { setEditingJob(job); setViewingJob(null); setJobDialogReadOnly(false); setShowJobDialog(true); }}
          onEmailDriver={emailDriver}
          unreadByJobId={unreadByJobId}
        />
      )}

      {/* ========== CANCEL CONFIRMATION ========== */}
      {cancelConfirmJob && (
        <Dialog open={!!cancelConfirmJob} onOpenChange={() => setCancelConfirmJob(null)}>
          <DialogContent className="bg-card border-border max-w-sm">
            <DialogHeader>
              <DialogTitle className="text-base">Cancel this job?</DialogTitle>
            </DialogHeader>
            <div className="space-y-2 py-2">
              <div className="text-sm">
                <span className="font-medium">{cancelConfirmJob.client_name}</span>
                <span className="text-muted-foreground"> · </span>
                <span style={{ fontFamily: "'IBM Plex Sans', monospace" }}>
                  {cancelConfirmJob.pickup_datetime ? formatMilFull(new Date(cancelConfirmJob.pickup_datetime).getHours(), new Date(cancelConfirmJob.pickup_datetime).getMinutes()) : ""}
                </span>
              </div>
              <div className="text-sm text-muted-foreground">{cancelConfirmJob.pickup_location} → {cancelConfirmJob.dropoff_location}</div>
              {cancelConfirmJob.payment_status === "invoice_sent" && (
                <div className="text-xs text-amber-400 bg-amber-500/10 border border-amber-500/30 rounded px-2 py-1.5 mt-1" data-testid="cancel-invoice-warning">
                  This job has an unpaid Square invoice. If the ride is canceled, void or cancel the invoice in Square Dashboard.
                </div>
              )}
            </div>
            <div className="flex justify-end gap-2 pt-2 border-t border-border">
              <Button variant="ghost" size="sm" onClick={() => setCancelConfirmJob(null)} data-testid="cancel-keep-button">Keep Job</Button>
              <Button size="sm" className="bg-red-600 hover:bg-red-700 text-white" onClick={() => cancelJob(cancelConfirmJob)} data-testid="cancel-confirm-button">Cancel Job</Button>
            </div>
          </DialogContent>
        </Dialog>
      )}

      {/* ========== JOB CREATE/EDIT/VIEW DIALOG ========== */}
      {showJobDialog && (
        <JobDialog
          key={`${jobDialogReadOnly ? "view" : "edit"}-${(editingJob || viewingJob)?.id || "new"}`}
          open={showJobDialog}
          onClose={() => {
            setShowJobDialog(false);
            setEditingJob(null);
            setViewingJob(null);
            setNewJobDate(null);
            setJobDialogReadOnly(false);
            // After close, refresh unread map (JobDialog auto-marks-read on open).
            api.get("/dispatch/jobs-with-unread-notes")
              .then(({ data }) => {
                const map = {};
                for (const row of data?.jobs || []) {
                  if (row?.job_id) map[row.job_id] = row.unread_count || 0;
                }
                setUnreadByJobId(map);
              })
              .catch(() => {});
          }}
          onSaved={handleJobSaved}
          drivers={drivers}
          vehicles={vehicles}
          job={editingJob || viewingJob}
          defaultDate={newJobDate}
          readOnly={jobDialogReadOnly}
          onEditRequest={() => { setEditingJob(viewingJob); setViewingJob(null); setJobDialogReadOnly(false); }}
        />
      )}
    </div>
  );
}
